import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { CoreContext } from "context/CoreContext";
import { buildS3DownloadUrl } from "services/s3Download";
import {
  CompleteLearningPathItem,
  ReadLearningPathDetails,
  StartLearningPath,
  StartLearningPathItem,
} from "services/learning-paths";
import { exposeStrapiError, parseStrapiImage } from "utils";
import { languages } from "utils/options";
import { buildPortalDetailRoute, getPortalConfig, normalizePortal } from "utils/portal";
import { isContentLockedError, openLockedContentModal } from "utils/lockedContent";
import { trackEvent } from "services/analytics";

const parseOptionalImage = (value) => {
  if (!value) return null;
  return parseStrapiImage(value);
};

const normalizeTrailItem = (item) => {
  const imageUrl = parseOptionalImage(item?.imageUrl || item?.image?.url || item?.cover);
  const documentUrl = parseOptionalImage(item?.documentUrl || (typeof item?.document === "string" ? item.document : item?.document?.url));

  return {
    ...item,
    cover: imageUrl,
    imageUrl,
    image: imageUrl ? { ...(item?.image || {}), url: imageUrl } : item?.image,
    document: item?.type === "documents" ? documentUrl : item?.document,
    documentUrl,
    file: item?.file,
    uid: item?.uid || (item?.type === "documents" ? "api::document.document" : "api::course.course"),
    document_type: item?.document_type || (item?.type === "documents" ? "Document" : undefined),
  };
};

const normalizeTrailStep = (step) => ({
  ...step,
  items: Array.isArray(step?.items) ? step.items.map(normalizeTrailItem) : [],
});

const normalizeTrail = (data) => {
  if (!data) return null;
  const normalizedSteps = Array.isArray(data?.steps) ? data.steps.map(normalizeTrailStep) : [];
  const normalizedItems = Array.isArray(data?.items)
    ? data.items.map(normalizeTrailItem)
    : normalizedSteps.flatMap((step) => step.items || []);
  const firstItemImage = normalizedItems.find((item) => item?.imageUrl)?.imageUrl;

  return {
    ...data,
    imageUrl: parseOptionalImage(data?.bannerImageUrl || data?.imageUrl || data?.image?.url) || firstItemImage || null,
    steps: normalizedSteps,
    items: normalizedItems,
    courses: Array.isArray(data?.courses) ? data.courses.map(normalizeTrailItem) : [],
    documents: Array.isArray(data?.documents) ? data.documents.map(normalizeTrailItem) : [],
  };
};

const appendTrailContext = (route, trailId, item) => {
  const params = new URLSearchParams();
  params.set("trailId", trailId);
  if (item?.stepId) params.set("stepId", item.stepId);
  if (item?.itemId) params.set("itemId", item.itemId);
  return `${route}?${params.toString()}`;
};

export default function useController(portal) {
  const history = useHistory();
  const { id } = useParams();
  const { language, setModal } = useContext(CoreContext);
  const resolvedPortal = normalizePortal(portal);
  const portalConfig = useMemo(() => getPortalConfig(resolvedPortal), [resolvedPortal]);
  const locale = useMemo(() => languages.find((item) => item.id === language)?.ref || "en", [language]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [loadingItemId, setLoadingItemId] = useState(null);
  const [trail, setTrail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await ReadLearningPathDetails(id, { portal: resolvedPortal, locale });
    if (isContentLockedError(result) && resolvedPortal === 'essentials') {
      setModal({ type: 'exclusive-content' });
      history.replace(portalConfig.homeRoute);
    } else if (!exposeStrapiError(result)) {
      setTrail(normalizeTrail(result?.data));
    }
    setLoading(false);
  }, [history, id, locale, portalConfig.homeRoute, resolvedPortal, setModal]);

  useEffect(() => {
    load();
  }, [load]);

  const navigateToItem = useCallback((item) => {
    if (!item) return;

    if (item?.type === "documents" || item?.itemType === "document") {
      const fileUrl = item?.documentUrl || item?.document || item?.file?.url;
      if (!fileUrl) return;

      trackEvent('PDF_OPEN', {
        content_type: item?.type || 'learning-path-document',
        content_id: item?.contentId || item?.id,
        resource_id: item?.file?.id || item?.itemId,
        content_name: item?.title,
        learning_path_id: id,
      });
      setModal({
        type: "pdf-preview",
        title: item?.title,
        url: buildS3DownloadUrl({
          sourceUrl: fileUrl,
          filename: item?.title || item?.file || item?.document || "document.pdf",
          disposition: 'inline',
        }),
      });
      return;
    }

    const route = buildPortalDetailRoute(resolvedPortal, item?.type || "courses", item?.contentId || item?.id);
    history.push(appendTrailContext(route, id, item));
  }, [history, id, resolvedPortal, setModal]);

  const openItem = useCallback(async (item) => {
    if (item?.accessState === 'locked') {
      openLockedContentModal(setModal, item);
      return;
    }
    if (!item?.itemId) {
      navigateToItem(item);
      return;
    }

    setLoadingItemId(item.itemId);
    const result = await StartLearningPathItem(id, item.itemId, { portal: resolvedPortal, locale });
    if (!exposeStrapiError(result)) {
      const nextTrail = normalizeTrail(result?.data);
      setTrail(nextTrail);
      const refreshedItem = nextTrail?.items?.find((entry) => `${entry?.itemId}` === `${item.itemId}`) || item;
      navigateToItem(refreshedItem);
    }
    setLoadingItemId(null);
  }, [id, locale, navigateToItem, resolvedPortal, setModal]);

  const completeItem = useCallback(async (item) => {
    if (!item?.itemId) return;

    setLoadingItemId(item.itemId);
    const result = await CompleteLearningPathItem(id, item.itemId, { portal: resolvedPortal, locale });
    if (!exposeStrapiError(result)) {
      const nextTrail = normalizeTrail(result?.data);
      setTrail(nextTrail);
      if (nextTrail?.nextItem) {
        const nextItem = nextTrail?.items?.find((entry) => `${entry?.itemId}` === `${nextTrail.nextItem?.itemId}`) || nextTrail.nextItem;
        navigateToItem(nextItem);
      }
    }
    setLoadingItemId(null);
  }, [id, locale, navigateToItem, resolvedPortal]);

  const continueTrail = useCallback(async () => {
    setStarting(true);
    const result = await StartLearningPath(id, { portal: resolvedPortal, locale });
    if (!exposeStrapiError(result)) {
      const nextTrail = normalizeTrail(result?.data);
      setTrail(nextTrail);
      if (nextTrail?.continueItem) {
        await openItem(nextTrail.continueItem);
      }
    }
    setStarting(false);
  }, [id, locale, openItem, resolvedPortal]);

  const goBack = useCallback(() => {
    history.push(`${portalConfig.basePath}/trails`);
  }, [history, portalConfig.basePath]);

  return {
    completeItem,
    continueTrail,
    goBack,
    loading,
    loadingItemId,
    openItem,
    starting,
    trail,
  };
}
